import { createApi } from "@reduxjs/toolkit/query/react";
import { APIErrorResponse, baseQueryWithRedirect, encodePostRequest, UntransformedResponse } from "./utils/apiUtils.ts";
import endpoints, { baseURL } from "../../data/api/v1/endpoints.ts";
import { GetCustomQuotesResponse, GetCustomQuotesResponseData } from "../../types/api/customQuotes/getCustomQuotes.ts";
import {
	GetCustomQuoteByIdResponse,
	GetCustomQuoteByIdResponseData
} from "types/api/customQuotes/getCustomQuoteById.ts";
import { GetQuotersResponse, GetQuotersResponseData } from "types/api/customQuotes/getQuoters.ts";
import {
	PostCustomQuoteRequest,
	PostCustomQuoteResponse,
	PostCustomQuoteResponseData
} from "types/api/customQuotes/postCustomQuote.ts";
import {
	DeclineCustomQuoteResponse,
	DeclineCustomQuoteResponseData
} from "types/api/customQuotes/declineCustomQuote.ts";
import {
	AddCustomQuoteCommentRequest,
	AddCustomQuoteCommentResponse,
	AddCustomQuoteCommentResponseData
} from "types/api/customQuotes/comments.ts";
import { UpdateRevisionResponse, UpdateRevisionResponseData } from "types/api/customQuotes/updateRevisions.ts";
import { models } from "types/api/viewModels.ts";
import { CreateRevisionResponse, CreateRevisionResponseData } from "types/api/customQuotes/createRevision.ts";
import { GetAutoCadFileRequestParameters, GetAutoCadFileResponse } from "types/api/customQuotes/getAutoCad.ts";
import {
	GetCqCodeComplexityScoresResponse,
	GetCqCodeComplexityScoresResponseData
} from "types/api/customQuotes/getCqCodeComplexityScores.ts";

export const customQuoteApiSlice = createApi({
	reducerPath: "customQuoteApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	tagTypes: ["CustomQuote", "CustomQuotes"],
	endpoints: (builder) => ({
		getCustomQuotes: builder.query<GetCustomQuotesResponseData, string | undefined>({
			query: (customQuoteId?: string) => endpoints.customQuotes.customQuotes(customQuoteId),
			transformResponse: (response: GetCustomQuotesResponse) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			}),
			providesTags: ["CustomQuotes"],
			keepUnusedDataFor: 30
		}),
		removeCustomQuote: builder.mutation({
			query: (customQuoteId: string) => ({
				url: endpoints.customQuotes.customQuotes(customQuoteId),
				method: "delete"
			}),
			invalidatesTags: ["CustomQuotes"]
		}),
		getCustomQuoteById: builder.query<GetCustomQuoteByIdResponseData, string | undefined>({
			query: (customQuoteId?: string) => endpoints.customQuotes.getCustomQuoteById(customQuoteId),
			transformResponse: (response: GetCustomQuoteByIdResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status
			}),
			providesTags: (result, error, id) => [{ type: "CustomQuote", id }]
		}),
		getQuoters: builder.query<GetQuotersResponseData, string | undefined>({
			query: () => {
				return endpoints.customQuotes.getQuoters();
			},
			transformResponse: (response: GetQuotersResponse) => {
				return response?.data;
			},
			transformErrorResponse: (error: { status: string | number }) => {
				return { status: error?.status };
			}
		}),
		addComment: builder.mutation<
			AddCustomQuoteCommentResponseData,
			{ id: string; comment: AddCustomQuoteCommentRequest }
		>({
			query: ({ id, comment }) => ({
				url: endpoints.customQuotes.comments(id),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(comment)
			}),
			transformResponse: (response: AddCustomQuoteCommentResponse) => {
				return response?.data;
			},
			transformErrorResponse: (error: any) => {
				return {
					status: error?.status,
					message: error?.data?.message || "Failure adding comment"
				};
			},
			invalidatesTags: (result, error, { id }) => [{ type: "CustomQuote", id }]
		}),

		updateCustomQuote: builder.mutation<GetCustomQuoteByIdResponseData, { id: string; assignedToId: string }>({
			query: ({ id, assignedToId }) => {
				return {
					url: endpoints.customQuotes.getCustomQuoteById(id),
					method: "PUT",
					body: { assignedToId }
				};
			},
			transformResponse: (response: GetCustomQuoteByIdResponse) => {
				return response?.data;
			},
			transformErrorResponse: (error: any) => {
				return {
					status: error?.status,
					message: error?.data?.message || "Update failed"
				};
			},
			invalidatesTags: (result, error, { id }) => [{ type: "CustomQuote", id }]
		}),
		postCustomQuote: builder.mutation<PostCustomQuoteResponseData, PostCustomQuoteRequest>({
			query: (customQuoteRequest) => ({
				url: endpoints.customQuotes.customQuotes(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(customQuoteRequest)
			}),
			transformResponse: (response: PostCustomQuoteResponse) => response.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				data: {
					errors: error?.data?.errors,
					status: error?.status
				}
			})
		}),
		declineCustomQuote: builder.mutation<
			DeclineCustomQuoteResponseData,
			{ quoteId: string; declineReasonCode: string; comment: string }
		>({
			query: ({ quoteId, declineReasonCode, comment }) => ({
				url: endpoints.customQuotes.declineCustomQuote(quoteId),
				method: "PUT",
				headers: {
					"Content-Type": "application/json"
				},
				body: encodePostRequest({ declineReasonCode, comment })
			}),
			transformResponse: (response: DeclineCustomQuoteResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status,
				errors: error?.data?.errors
			}),
			invalidatesTags: (result, error, { quoteId }) => [{ type: "CustomQuote", id: quoteId }]
		}),
		createRevision: builder.mutation<
			CreateRevisionResponseData,
			{ quoteId: string; revision: models["CreateRevisionRequest"] }
		>({
			query: ({ quoteId, revision }) => ({
				url: endpoints.customQuotes.createRevision(quoteId),
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: encodePostRequest(revision)
			}),
			transformResponse: (response: CreateRevisionResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status,
				errors: error?.data?.errors
			}),
			invalidatesTags: (result, error, { quoteId }) => [{ type: "CustomQuote", id: quoteId }]
		}),
		updateRevision: builder.mutation<
			UpdateRevisionResponseData,
			{ quoteId: string; revisionId: number; revision: models["UpdateRevisionRequest"] }
		>({
			query: ({ quoteId, revisionId, revision }) => ({
				url: endpoints.customQuotes.revisions(quoteId, revisionId),
				method: "PUT",
				headers: {
					"Content-Type": "application/json"
				},
				body: encodePostRequest(revision)
			}),
			transformResponse: (response: UpdateRevisionResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status,
				errors: error?.data?.errors
			}),
			invalidatesTags: (result, error, { quoteId }) => [{ type: "CustomQuote", id: quoteId }]
		}),
		getAutoCadFile: builder.query<GetAutoCadFileResponse, GetAutoCadFileRequestParameters>({
			query: (params?: GetAutoCadFileRequestParameters) => ({
				url: endpoints.customQuotes.getAutocadFile(params?.quoteId ?? "", params?.revisionId ?? 0),
				method: "GET",
				responseHandler: (response: Response) => (response.ok ? response.blob() : response.json())
			}),
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status.toString()
			}),
			keepUnusedDataFor: 0
		}),
		deleteRevision: builder.mutation<{}, { quoteId: string; revisionId: number }>({
			query: ({ quoteId, revisionId }) => ({
				url: endpoints.customQuotes.revisions(quoteId, revisionId),
				method: "delete"
			}),
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status.toString()
			}),
			invalidatesTags: (result, error, { quoteId }) => [{ type: "CustomQuote", quoteId }]
		}),
		getComplexityScores: builder.query<GetCqCodeComplexityScoresResponseData, string>({
			query: (sku: string) => endpoints.customQuotes.getComplexityScores(sku),
			transformResponse: (response: GetCqCodeComplexityScoresResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		})
	})
});

export const {
	useGetCustomQuoteByIdQuery,
	useUpdateCustomQuoteMutation,
	useGetQuotersQuery,
	useLazyGetCustomQuotesQuery,
	useRemoveCustomQuoteMutation,
	usePostCustomQuoteMutation,
	useDeclineCustomQuoteMutation,
	useAddCommentMutation,
	useCreateRevisionMutation,
	useUpdateRevisionMutation,
	useLazyGetAutoCadFileQuery,
	useDeleteRevisionMutation,
	useGetComplexityScoresQuery,
	useLazyGetComplexityScoresQuery
} = customQuoteApiSlice;
